import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/UI/card';
import { Button } from '@/components/UI/button';
import { Download, Loader2, Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/UI/input';
import { toast } from '@/components/UI/use-toast';
import axios from 'axios';

// NEW BACKEND for SOA functionality
const NEW_API_URL = 'https://accountss-3.onrender.com';

// Professional faint colors for different clients
const CLIENT_COLORS = [
  'bg-blue-50 hover:bg-blue-100',
  'bg-green-50 hover:bg-green-100', 
  'bg-purple-50 hover:bg-purple-100',
  'bg-orange-50 hover:bg-orange-100',
  'bg-pink-50 hover:bg-pink-100',
  'bg-indigo-50 hover:bg-indigo-100',
  'bg-teal-50 hover:bg-teal-100',
  'bg-amber-50 hover:bg-amber-100',
  'bg-red-50 hover:bg-red-100',
  'bg-cyan-50 hover:bg-cyan-100',
  'bg-emerald-50 hover:bg-emerald-100',
  'bg-violet-50 hover:bg-violet-100'
];

const StatementOfAccount = () => {
  const [soaData, setSOAData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [filteredData, setFilteredData] = useState({ rows: [], totals: {} });

  // Generate client color mapping
  const clientColorMap = useMemo(() => {
    const uniqueClients = [...new Set(soaData.rows.map(row => row.client_name))].sort();
    const colorMap = {};
    uniqueClients.forEach((client, index) => {
      colorMap[client] = CLIENT_COLORS[index % CLIENT_COLORS.length];
    });
    return colorMap;
  }, [soaData.rows]);

  // Get unique clients for filter dropdown
  const uniqueClients = useMemo(() => {
    return [...new Set(soaData.rows.map(row => row.client_name))].sort();
  }, [soaData.rows]);

  useEffect(() => {
    fetchSOAData();
  }, []);

  useEffect(() => {
    filterData();
  }, [searchTerm, selectedClient, soaData]);

  const fetchSOAData = async () => {
    try {
      setLoading(true);
      console.log('Fetching SOA data from NEW backend:', `${NEW_API_URL}/api/soa`);
      
      const response = await axios.get(`${NEW_API_URL}/api/soa`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Accept': 'application/json'
        }
      });

      console.log('SOA Response:', response.data);

      if (response.data.status === 'success') {
        setSOAData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch SOA data');
      }
    } catch (error) {
      console.error('Error fetching SOA data:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch SOA data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = soaData.rows;

    // Apply client filter
    if (selectedClient) {
      filtered = filtered.filter(row => row.client_name === selectedClient);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(row => 
        row.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.tax_invoice_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.project_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.project_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by serial number in DESCENDING order (highest to lowest)
    filtered = filtered.sort((a, b) => {
      const serialA = parseInt(a.serial_number) || 0;
      const serialB = parseInt(b.serial_number) || 0;
      return serialB - serialA; // Descending order (newest first)
    });

    // Recalculate totals for filtered data
    const recalculatedTotals = filtered.reduce((acc, row) => ({
      sales: acc.sales + (row.cumulative_value || 0),
      retentionDeduction: acc.retentionDeduction + (row.cumulative_retention_total || 0),
      performanceRetentionDeduction: acc.performanceRetentionDeduction + (row.cumulative_performance_retention_total || 0),
      advanceDeduction: acc.advanceDeduction + (row.advance_release_total || 0),
      retentionRelease: acc.retentionRelease + (row.retention_release_total || 0),
      ncrDeduction: acc.ncrDeduction + (row.ncr_deduction_total || 0),
      otherDeduction: acc.otherDeduction + (row.other_deduction_total || 0),
      previousPayment: acc.previousPayment + (row.previous_payment_total || 0),
      amountWithoutVat: acc.amountWithoutVat + (row.amount_without_vat || 0),
      vatAmount: acc.vatAmount + (row.vat_amount || 0),
      totalAmount: acc.totalAmount + (row.amount || 0)
    }), {
      sales: 0,
      retentionDeduction: 0,
      performanceRetentionDeduction: 0,
      advanceDeduction: 0,
      retentionRelease: 0,
      ncrDeduction: 0,
      otherDeduction: 0,
      previousPayment: 0,
      amountWithoutVat: 0,
      vatAmount: 0,
      totalAmount: 0
    });

    setFilteredData({ rows: filtered, totals: recalculatedTotals });
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      console.log('Downloading SOA from NEW backend:', `${NEW_API_URL}/api/soa/download`);
      
      const response = await axios.get(`${NEW_API_URL}/api/soa/download`, {
        headers: {
          'Authorization': localStorage.getItem('token')
        },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement_of_Account_${selectedClient ? `${selectedClient}_` : ''}${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "SOA downloaded successfully",
      });
    } catch (error) {
      console.error('Error downloading SOA:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to download SOA",
        variant: "destructive"
      });
    } finally {
      setDownloading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedClient('');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return new Intl.NumberFormat('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(value));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
        <span className="ml-2">Loading Statement of Account...</span>
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-xl font-semibold">Statement of Account</CardTitle>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download Excel
            </Button>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <div className="relative flex-1 max-w-xs">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Clients</option>
                {uniqueClients.map(client => (
                  <option key={client} value={client}>
                    {client}
                  </option>
                ))}
              </select>
            </div>
            
            {(searchTerm || selectedClient) && (
              <Button
                onClick={clearFilters}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>

          {/* Active Filters Display */}
          {(searchTerm || selectedClient) && (
            <div className="flex flex-wrap gap-2 text-sm">
              {searchTerm && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                  Search: "{searchTerm}"
                </span>
              )}
              {selectedClient && (
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
                  Client: {selectedClient}
                </span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="border border-slate-300 px-2 py-3 text-left font-semibold text-slate-700 bg-slate-100">S.No</th>
                <th className="border border-slate-300 px-2 py-3 text-left font-semibold text-slate-700 bg-slate-100">Inv Ref</th>
                <th className="border border-slate-300 px-2 py-3 text-left font-semibold text-slate-700 bg-slate-100">Date</th>
                <th className="border border-slate-300 px-2 py-3 text-left font-semibold text-slate-700 bg-slate-100">Client</th>
                <th className="border border-slate-300 px-2 py-3 text-left font-semibold text-slate-700 bg-slate-100">Project</th>
                <th className="border border-slate-300 px-2 py-3 text-left font-semibold text-slate-700 bg-slate-100">PC#</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Sales</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Retention</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">P.Retention</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Advance</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Ret.Release</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">NCR Ded.</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Other Ded.</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Prev.Payment</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Amount W/o VAT</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">VAT Amount</th>
                <th className="border border-slate-300 px-2 py-3 text-right font-semibold text-slate-700 bg-slate-100">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.rows.length === 0 ? (
                <tr>
                  <td colSpan="17" className="border border-gray-300 px-4 py-6 text-center text-gray-500">
                    {searchTerm || selectedClient ? 'No invoices match your search criteria' : 'No invoice data available'}
                  </td>
                </tr>
              ) : (
                filteredData.rows.map((row, index) => (
                  <tr 
                    key={row.serial_number} 
                    className={`transition-colors duration-150 ${clientColorMap[row.client_name] || 'bg-gray-50 hover:bg-gray-100'} border-b border-gray-200`}
                  >
                    <td className="border-l border-r border-gray-300 px-2 py-0.5 text-center font-medium">{row.serial_number}</td>
                    <td className="border-r border-gray-300 px-2 py-0.5">{row.tax_invoice_ref}</td>
                    <td className="border-r border-gray-300 px-2 py-0.5">{formatDate(row.tax_invoice_date)}</td>
                    <td className="border-r border-gray-300 px-2 py-0.5 max-w-32">
                      <div className="truncate font-medium" title={row.client_name}>
                        {row.client_name}
                      </div>
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-blue-700">{row.project_code}</span>
                        <span className="text-gray-600 truncate max-w-40 text-xs" title={row.project_name}>
                          {row.project_name}
                        </span>
                      </div>
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-center">{row.payment_certificate_no}</td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right font-medium">
                      {formatCurrency(row.cumulative_value)}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-red-600">
                      {row.cumulative_retention_total > 0 ? `(${formatCurrency(row.cumulative_retention_total)})` : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-red-600">
                      {row.cumulative_performance_retention_total > 0 ? `(${formatCurrency(row.cumulative_performance_retention_total)})` : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-red-600">
                      {row.advance_release_total > 0 ? `(${formatCurrency(row.advance_release_total)})` : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-green-600">
                      {row.retention_release_total > 0 ? formatCurrency(row.retention_release_total) : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-red-600">
                      {row.ncr_deduction_total > 0 ? `(${formatCurrency(row.ncr_deduction_total)})` : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-red-600">
                      {row.other_deduction_total > 0 ? `(${formatCurrency(row.other_deduction_total)})` : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right text-red-600">
                      {row.previous_payment_total > 0 ? `(${formatCurrency(row.previous_payment_total)})` : '-'}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right font-medium">
                      {formatCurrency(row.amount_without_vat)}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right">
                      {formatCurrency(row.vat_amount)}
                    </td>
                    <td className="border-r border-gray-300 px-2 py-0.5 text-right font-semibold text-blue-900">
                      {formatCurrency(row.amount)}
                    </td>
                  </tr>
                ))
              )}
              
              {/* Totals Row - Only show if there's data */}
              {filteredData.rows.length > 0 && (
                <tr className="bg-slate-200 font-bold border-t-2 border-slate-400 sticky bottom-0 z-10">
                  <td colSpan="6" className="border border-slate-300 px-2 py-1.5 text-center font-bold text-slate-800 bg-slate-200">
                    TOTALS ({filteredData.rows.length} invoices)
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-slate-800 bg-slate-200">
                    {formatCurrency(filteredData.totals.sales)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-red-700 bg-slate-200">
                    {filteredData.totals.retentionDeduction > 0 ? `(${formatCurrency(filteredData.totals.retentionDeduction)})` : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-red-700 bg-slate-200">
                    {filteredData.totals.performanceRetentionDeduction > 0 ? `(${formatCurrency(filteredData.totals.performanceRetentionDeduction)})` : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-red-700 bg-slate-200">
                    {filteredData.totals.advanceDeduction > 0 ? `(${formatCurrency(filteredData.totals.advanceDeduction)})` : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-green-700 bg-slate-200">
                    {filteredData.totals.retentionRelease > 0 ? formatCurrency(filteredData.totals.retentionRelease) : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-red-700 bg-slate-200">
                    {filteredData.totals.ncrDeduction > 0 ? `(${formatCurrency(filteredData.totals.ncrDeduction)})` : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-red-700 bg-slate-200">
                    {filteredData.totals.otherDeduction > 0 ? `(${formatCurrency(filteredData.totals.otherDeduction)})` : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-red-700 bg-slate-200">
                    {filteredData.totals.previousPayment > 0 ? `(${formatCurrency(filteredData.totals.previousPayment)})` : '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-slate-800 bg-slate-200">
                    {formatCurrency(filteredData.totals.amountWithoutVat)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-slate-800 bg-slate-200">
                    {formatCurrency(filteredData.totals.vatAmount)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-bold text-blue-900 bg-blue-100">
                    {formatCurrency(filteredData.totals.totalAmount)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer Summary - Only show if there's data */}
        {filteredData.rows.length > 0 && (
          <div className="p-3 bg-gray-50 border-t">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Showing:</span>
                <span className="ml-1 font-semibold">{filteredData.rows.length} of {soaData.rows.length} invoices</span>
              </div>
              <div>
                <span className="text-gray-600">Clients:</span>
                <span className="ml-1 font-semibold">{selectedClient || `${uniqueClients.length} total`}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Deductions:</span>
                <span className="ml-1 font-semibold text-red-600">
                  AED {formatCurrency(
                    filteredData.totals.retentionDeduction + 
                    filteredData.totals.performanceRetentionDeduction + 
                    filteredData.totals.advanceDeduction + 
                    filteredData.totals.ncrDeduction + 
                    filteredData.totals.otherDeduction + 
                    filteredData.totals.previousPayment
                  )}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Final Amount:</span>
                <span className="ml-1 font-bold text-blue-900">AED {formatCurrency(filteredData.totals.totalAmount)}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StatementOfAccount;